'use client';

/**
 * Newsletter API helper — frontend client for the public newsletter
 * subscribe endpoint (SPEC-135 §3).
 *
 * Endpoint: `POST /api/newsletter/subscribe`
 * Auth: NONE (public — no JWT). Uses `auth: false` so `apiFetch` skips
 *       the access-token header (the endpoint accepts anonymous traffic).
 * Rate limit: 3/hour/IP (enforced server-side).
 *
 * The endpoint returns `{ message: string }` with HTTP 201 on first
 * subscription (email confirmation queued) or HTTP 200 if the email is
 * already confirmed (no re-send to avoid bombing).
 *
 * Spec ref: docs/SPEC-135-FOOTER-MISION-LEGALES.md §3 (endpoints API).
 *
 * Anti-P-023: returns real values (message string) for the caller to render.
 * Anti-P-041: 0 `as any` casts.
 * Anti-P-042: 0 `console.*` statements.
 */
import { apiFetch } from './api-client';

export interface NewsletterSubscribeResult {
  message: string;
}

/**
 * Subscribe an email to the Profileia newsletter (double opt-in).
 * Throws `ApiClientError` on 4xx/5xx so the caller can render the error.
 */
export async function subscribeToNewsletter(email: string): Promise<NewsletterSubscribeResult> {
  return apiFetch<NewsletterSubscribeResult>('/newsletter/subscribe', {
    method: 'POST',
    auth: false,
    body: JSON.stringify({ email }),
    // SPEC-177 Task B.2 (Sprint 91): explicit 5s timeout. The backend
    // now fire-and-forgets the email send (Task B.1) so the HTTP
    // response should return in <500ms (just Mongo upsert + decrypt).
    // If the backend takes >5s, something is wrong (Mongo hang, network
    // partition, etc.) — abort so the user sees the TIMEOUT message
    // ("Estamos procesando tu suscripción...") instead of hanging
    // indefinitely. The subscriber IS persisted server-side even on
    // timeout, so the user can safely retry.
    timeoutMs: 5_000,
  });
}
